import React, { useState, useEffect, useRef } from 'react';
import { 
  Card, Table, Button, Space, Input, Form, Select, DatePicker, 
  Modal, message, Tag, Tooltip, Popconfirm
} from 'antd';
import { 
  SearchOutlined, DeleteOutlined, ExclamationCircleOutlined, 
  EyeOutlined, ReloadOutlined, ClearOutlined
} from '@ant-design/icons';
import { useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { 
  getJobLogList, 
  deleteJobLog, 
  batchDeleteJobLogs, 
  clearJobLogs, 
  getJobLogById 
} from '@/api/modules/job';
import type { JobLogItem } from '@/api/modules/job';

const { confirm } = Modal;
const { RangePicker } = DatePicker;

const JobLogList: React.FC = () => {
  const [searchParams] = useSearchParams();
  const jobId = searchParams.get('jobId');

  const [list, setList] = useState<JobLogItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
  });
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [logDetailVisible, setLogDetailVisible] = useState(false);
  const [currentLog, setCurrentLog] = useState<JobLogItem | null>(null);
  const [searchForm] = Form.useForm();
  
  // 初始化表单
  useEffect(() => {
    if (jobId) {
      searchForm.setFieldsValue({ jobId });
    }
  }, [jobId, searchForm]);

  // 获取日志列表
  const fetchLogList = async () => {
    setLoading(true);
    try {
      const params = {
        ...searchForm.getFieldsValue(),
        page: pagination.current,
        limit: pagination.pageSize,
      };
      
      // 处理日期范围
      if (params.dateRange) {
        const [startDate, endDate] = params.dateRange;
        params.startDate = dayjs(startDate).format('YYYY-MM-DD HH:mm:ss');
        params.endDate = dayjs(endDate).format('YYYY-MM-DD HH:mm:ss');
        delete params.dateRange;
      }
      
      const res = await getJobLogList(params);
      if (res && res.data) {
        const items = (res.data as any)?.data ?? res.data;
        setList(Array.isArray(items) ? items : []);
        setTotal(res.total || 0);
      }
    } catch (error) {
      console.error('获取任务日志列表失败', error);
    } finally {
      setLoading(false);
    }
  };

  // 初始化加载
  useEffect(() => {
    fetchLogList();
  }, [pagination.current, pagination.pageSize]);

  // 处理表格变更
  const handleTableChange = (pagination: any) => {
    setPagination({
      current: pagination.current,
      pageSize: pagination.pageSize,
    });
  };

  // 搜索
  const handleSearch = () => {
    setPagination({ ...pagination, current: 1 });
    fetchLogList();
  };

  // 重置搜索
  const handleReset = () => {
    searchForm.resetFields();
    if (jobId) {
      searchForm.setFieldsValue({ jobId });
    }
    setPagination({ ...pagination, current: 1 });
    fetchLogList();
  };

  // 查看日志详情
  const handleViewLog = async (id: string) => {
    try {
      const res = await getJobLogById(id);
      if (res && res.data) {
        const logItem = (res.data as any)?.data ?? res.data;
        setCurrentLog(logItem as JobLogItem);
        setLogDetailVisible(true);
      }
    } catch (error) {
      console.error('获取日志详情失败', error);
    }
  };

  // 删除日志
  const handleDeleteLog = async (id: string) => {
    try {
      await deleteJobLog(id);
      message.success('删除成功');
      fetchLogList();
      setSelectedRowKeys([]);
    } catch (error) {
      console.error('删除失败', error);
    }
  };

  // 批量删除
  const handleBatchDelete = () => {
    if (!selectedRowKeys.length) {
      message.warning('请至少选择一条记录');
      return;
    }

    confirm({
      title: '确定要删除所选日志吗?',
      icon: <ExclamationCircleOutlined />,
      content: '此操作将永久删除所选日志记录，是否继续？',
      onOk: async () => {
        try {
          await batchDeleteJobLogs(selectedRowKeys.map(key => key.toString()));
          message.success('删除成功');
          fetchLogList();
          setSelectedRowKeys([]);
        } catch (error) {
          console.error('删除失败', error);
        }
      }
    });
  };

  // 清空所有日志
  const handleClearAllLogs = () => {
    confirm({
      title: '确定要清空所有任务日志吗?',
      icon: <ExclamationCircleOutlined />,
      content: '此操作将永久删除所有任务日志记录，是否继续？',
      onOk: async () => {
        try {
          await clearJobLogs();
          message.success('清空成功');
          fetchLogList();
          setSelectedRowKeys([]);
        } catch (error) {
          console.error('清空失败', error);
        }
      }
    });
  };

  const columns = [
    {
      title: '日志ID',
      dataIndex: 'id',
      width: 80,
    },
    {
      title: '任务名称',
      dataIndex: 'jobName',
      ellipsis: true,
    },
    {
      title: '任务组',
      dataIndex: 'jobGroup',
    },
    {
      title: '调用目标',
      dataIndex: 'invokeTarget',
      ellipsis: true,
      render: (text: string) => (
        <Tooltip placement="topLeft" title={text}>
          <span>{text}</span>
        </Tooltip>
      )
    },
    {
      title: '执行状态',
      dataIndex: 'status',
      render: (status: string) => (
        <Tag color={status === 'success' ? 'success' : 'error'}>
          {status === 'success' ? '成功' : '失败'}
        </Tag>
      )
    },
    {
      title: '执行时长(毫秒)',
      dataIndex: 'duration',
      sorter: true,
    },
    {
      title: '开始时间',
      dataIndex: 'startTime',
      sorter: true,
    },
    {
      title: '操作',
      key: 'action',
      width: 150,
      render: (_: any, record: JobLogItem) => (
        <Space size="small">
          <Button
            type="link"
            icon={<EyeOutlined />}
            size="small"
            onClick={() => handleViewLog(record.id)}
          >
            详情
          </Button>
          
          <Popconfirm
            title="确定要删除此日志吗?"
            onConfirm={() => handleDeleteLog(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Button 
              type="link" 
              danger 
              icon={<DeleteOutlined />} 
              size="small"
            >
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div className="job-log-list-container">
      <Card
        title="任务执行日志"
        bordered={false}
      >
        {/* 搜索表单 */}
        <Form
          form={searchForm}
          layout="inline"
          onFinish={handleSearch}
          style={{ marginBottom: 16 }}
        >
          <Form.Item name="jobId">
            <Input placeholder="任务ID" allowClear />
          </Form.Item>
          <Form.Item name="jobName">
            <Input placeholder="任务名称" allowClear />
          </Form.Item>
          <Form.Item name="status">
            <Select
              placeholder="执行状态"
              style={{ width: 120 }}
              allowClear
            >
              <Select.Option value="success">成功</Select.Option>
              <Select.Option value="fail">失败</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item name="dateRange">
            <RangePicker
              showTime
              format="YYYY-MM-DD HH:mm:ss"
              placeholder={['开始时间', '结束时间']}
            />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button 
                type="primary" 
                htmlType="submit"
                icon={<SearchOutlined />}
              >
                搜索
              </Button>
              <Button 
                onClick={handleReset}
                icon={<ReloadOutlined />}
              >
                重置
              </Button>
            </Space>
          </Form.Item>
        </Form>

        {/* 工具栏 */}
        <div style={{ marginBottom: 16 }}>
          <Space>
            <Button
              danger
              icon={<DeleteOutlined />}
              onClick={handleBatchDelete}
              disabled={!selectedRowKeys.length}
            >
              批量删除
            </Button>
            <Button
              danger
              icon={<ClearOutlined />}
              onClick={handleClearAllLogs}
            >
              清空日志
            </Button>
          </Space>
        </div>

        {/* 表格 */}
        <Table
          rowKey="id"
          columns={columns}
          dataSource={list}
          pagination={{
            ...pagination,
            total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `共 ${total} 条`,
          }}
          loading={loading}
          rowSelection={{
            selectedRowKeys,
            onChange: (keys) => setSelectedRowKeys(keys),
          }}
          onChange={handleTableChange}
        />
      </Card>

      {/* 日志详情对话框 */}
      <Modal
        title="执行日志详情"
        open={logDetailVisible}
        onCancel={() => setLogDetailVisible(false)}
        footer={[
          <Button key="close" onClick={() => setLogDetailVisible(false)}>
            关闭
          </Button>
        ]}
        width={700}
      >
        {currentLog && (
          <div>
            <p><strong>任务名称:</strong> {currentLog.jobName}</p>
            <p><strong>任务组:</strong> {currentLog.jobGroup}</p>
            <p><strong>调用目标:</strong> {currentLog.invokeTarget}</p>
            <p>
              <strong>执行状态:</strong> 
              <Tag color={currentLog.status === 'success' ? 'success' : 'error'} style={{ marginLeft: 8 }}>
                {currentLog.status === 'success' ? '成功' : '失败'}
              </Tag>
            </p>
            <p><strong>执行耗时:</strong> {currentLog.duration}毫秒</p>
            <p><strong>开始时间:</strong> {currentLog.startTime}</p>
            <p><strong>结束时间:</strong> {currentLog.endTime}</p>
            
            {currentLog.exceptionInfo && (
              <>
                <p><strong>异常信息:</strong></p>
                <div 
                  style={{ 
                    backgroundColor: '#fff2f0', 
                    padding: 12, 
                    border: '1px solid #ffccc7',
                    borderRadius: 4,
                    overflow: 'auto',
                    maxHeight: 300
                  }}
                >
                  <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
                    {currentLog.exceptionInfo}
                  </pre>
                </div>
              </>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default JobLogList;
